import React, { useRef, useState } from 'react';
import { Box, Stack, TextField, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import TitleIcon from '@mui/icons-material/Title';
import Markdown from './Markdown';
import { C, FONT, hexToRgba } from '../theme/tokens';

/**
 * A markdown note field: write, preview, and a toolbar of the six marks
 * people actually reach for.
 *
 * Write and Preview are two states of one box rather than a split view —
 * a note is short, and a side-by-side preview would halve the writing width
 * for a formatting language you can already read in the source.
 *
 * The toolbar wraps the selection rather than inserting placeholder text, so
 * select-then-bold behaves the way it does in every other editor. With no
 * selection it inserts the marks and puts the caret between them.
 */

interface Mark {
  key: string;
  title: string;
  icon: React.ReactNode;
  /** Wraps the selection. */
  wrap?: [string, string];
  /** Prefixes each selected line. */
  prefix?: string;
}

const MARKS: Mark[] = [
  { key: 'bold', title: 'Bold', icon: <FormatBoldIcon sx={{ fontSize: 15 }} />, wrap: ['**', '**'] },
  { key: 'italic', title: 'Italic', icon: <FormatItalicIcon sx={{ fontSize: 15 }} />, wrap: ['*', '*'] },
  { key: 'code', title: 'Code', icon: <CodeIcon sx={{ fontSize: 15 }} />, wrap: ['`', '`'] },
  { key: 'heading', title: 'Heading', icon: <TitleIcon sx={{ fontSize: 15 }} />, prefix: '## ' },
  { key: 'list', title: 'Bulleted list', icon: <FormatListBulletedIcon sx={{ fontSize: 15 }} />, prefix: '- ' },
  { key: 'quote', title: 'Quote', icon: <FormatQuoteIcon sx={{ fontSize: 15 }} />, prefix: '> ' },
];

const MarkdownField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  /** Fired on blur, the same save-on-blur contract the other fields use. */
  onCommit: () => void;
  placeholder?: string;
  minRows?: number;
}> = ({ value, onChange, onCommit, placeholder, minRows = 5 }) => {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const apply = (mark: Mark) => {
    const el = inputRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const selected = value.slice(start, end);

    let next: string;
    let caret: [number, number];

    if (mark.prefix) {
      // Line marks apply from the start of the first selected line, so
      // hitting "list" mid-sentence still produces a list item.
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const body = value.slice(lineStart, end) || '';
      const marked = body
        .split('\n')
        .map((l) => (l.startsWith(mark.prefix!) ? l : `${mark.prefix}${l}`))
        .join('\n');
      next = value.slice(0, lineStart) + marked + value.slice(end);
      caret = [lineStart + marked.length, lineStart + marked.length];
    } else {
      const [open, close] = mark.wrap!;
      next = value.slice(0, start) + open + selected + close + value.slice(end);
      caret = selected
        ? [start + open.length, start + open.length + selected.length]
        : [start + open.length, start + open.length];
    }

    onChange(next);
    // The value lands on the next render, so the caret has to wait for it.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret[0], caret[1]);
    });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
        {MARKS.map((m) => (
          <Tooltip key={m.key} title={m.title} disableInteractive>
            <Box
              component="button"
              type="button"
              aria-label={m.title}
              // Mousedown, not click: click fires after the textarea has
              // already lost focus and thrown away its selection range.
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                apply(m);
              }}
              disabled={tab === 'preview'}
              sx={toolSx}
            >
              {m.icon}
            </Box>
          </Tooltip>
        ))}
        <Box sx={{ flex: 1 }} />
        {(['write', 'preview'] as const).map((t) => (
          <Box key={t} component="button" type="button" onClick={() => setTab(t)} sx={tabSx(tab === t)}>
            {t}
          </Box>
        ))}
      </Stack>

      {tab === 'write' ? (
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={minRows}
          value={value}
          placeholder={placeholder}
          inputRef={inputRef}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          sx={{ '& .MuiInputBase-input': { fontFamily: FONT.body, fontSize: '0.86rem', lineHeight: 1.6 } }}
        />
      ) : (
        // Same border, radius and padding as the input it replaces, so
        // toggling doesn't move anything on the page.
        <Box
          sx={{
            border: `1px solid ${C.hairline}`,
            borderRadius: '6px',
            px: 1.75,
            py: 1.25,
            minHeight: minRows * 22 + 20,
          }}
        >
          {value.trim() ? (
            <Markdown text={value} />
          ) : (
            <Typography sx={{ color: C.textDim, fontSize: '0.84rem' }}>Nothing to preview yet.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const toolSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 24,
  p: 0,
  borderRadius: '4px',
  cursor: 'pointer',
  background: 'transparent',
  border: `1px solid transparent`,
  color: C.textDim,
  transition: 'color 0.15s ease, border-color 0.15s ease',
  '&:hover:not(:disabled)': { color: C.cyan, borderColor: hexToRgba(C.cyan, 0.4) },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
} as const;

const tabSx = (active: boolean) =>
  ({
    fontFamily: FONT.sub,
    fontSize: '0.66rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    px: 1,
    py: '3px',
    borderRadius: '4px',
    cursor: 'pointer',
    background: 'transparent',
    border: `1px solid ${active ? hexToRgba(C.cyan, 0.5) : 'transparent'}`,
    color: active ? C.cyan : C.textDim,
    transition: 'color 0.15s ease, border-color 0.15s ease',
    '&:hover': { color: C.cyan },
  }) as const;

export default MarkdownField;
